import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../ui/command";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../ui/sheet";
import { ChevronDown, Check, Search, X } from "lucide-react";

export const TRADE_LABEL = {
  painting: "Schilderwerk",
  flooring: "Vloer",
  plastering: "Pleisterwerk",
  inspection: "Inspectie",
  other: "Overig",
};

const FILTERS = [
  { key: "all", label: "Alles", match: () => true },
  { key: "voorbereiding", label: "Voorbereiding", match: (p) => /voorbereid/i.test(p.work_category || "") },
  { key: "schilderwerk", label: "Schilderwerk", match: (p) => /schilder/i.test(p.work_category || "") && !/gevel/i.test(p.work_category || "") },
  { key: "lakwerk", label: "Lakwerk", match: (p) => /lak|vernis|beits/i.test(p.work_category || "") },
  { key: "gevelwerk", label: "Gevelwerk", match: (p) => /gevel|buitenschrijn/i.test(p.work_category || "") },
  { key: "extra", label: "Extra", match: (p) => !p.work_category || /extra|overig|andere/i.test(p.work_category || "") },
];

// v6 F6 — UX fix: prijscode-dropdown was onleesbaar op mobiel (te smal,
// chaotische line-breaks). Op mobiel renderen we nu een bottom-sheet
// (full-width, hoog, scrollbaar) met items in een 2-regel layout. Op
// desktop behouden we de bestaande Popover — exact dezelfde logica.
function useIsMobile(breakpointPx = 640) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${breakpointPx}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [breakpointPx]);
  return isMobile;
}

export default function CodeCombobox({ value, priceCodes, onSelect, testId }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const isMobile = useIsMobile();
  const selected = value ? priceCodes.find((p) => p.id === value) : null;

  const activeFilter = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const filtered = priceCodes.filter(activeFilter.match);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of filtered) {
      const t = p.work_category || TRADE_LABEL[p.trade_category] || "Overig";
      (g[t] = g[t] || []).push(p);
    }
    return g;
    // eslint-disable-next-line
  }, [filter, priceCodes]);

  // Mobile-only: client-side search filter (Command's built-in CMD-K matcher
  // is great, but on mobile we want a simple, predictable big input).
  const searchedGrouped = useMemo(() => {
    if (!search.trim()) return grouped;
    const q = search.toLowerCase();
    const out = {};
    for (const [cat, codes] of Object.entries(grouped)) {
      const m = codes.filter(
        (p) =>
          (p.code || "").toLowerCase().includes(q) ||
          (p.description || "").toLowerCase().includes(q) ||
          (p.work_category || "").toLowerCase().includes(q),
      );
      if (m.length) out[cat] = m;
    }
    return out;
  }, [search, grouped]);

  // Reset search when closing
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const trigger = (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className="h-11 mt-1.5 rounded-xl w-full justify-between font-normal"
      data-testid={testId}
      onClick={() => setOpen(true)}
    >
      <span className="truncate text-left">
        {selected ? (
          <>
            <span className="font-mono text-xs mr-2">{selected.code}</span>
            <span className="text-[#4F4F4D]">{selected.description}</span>
          </>
        ) : (
          <span className="text-[#4F4F4D]">Kies code...</span>
        )}
      </span>
      <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
    </Button>
  );

  // Reusable item renderer — mobile-optimized: omschrijving boven,
  // code+categorie+prijs eronder.
  const ItemRow = ({ p, selectedId }) => (
    <div className="flex items-start gap-2 w-full py-1">
      <Check
        className={`w-4 h-4 mt-1 flex-shrink-0 ${
          selectedId === p.id ? "opacity-100 text-[#D96C4A]" : "opacity-0"
        }`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[#2C2C2A] text-sm leading-snug font-medium break-words">
          {p.description}
        </div>
        <div className="flex items-center justify-between gap-2 mt-1 flex-wrap">
          <div className="text-[10px] font-mono text-[#5D7259] truncate">
            {p.code}
            {p.work_category && (
              <>
                <span className="mx-1 text-[#A8A7A0]">·</span>
                <span className="text-[#4F4F4D]">{p.work_category}</span>
              </>
            )}
          </div>
          {p.default_price != null && (
            <span className="text-xs font-semibold text-[#7F4124] whitespace-nowrap">
              € {Number(p.default_price).toFixed(2)} / {p.unit}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  // -------- Mobile path: bottom Sheet --------
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        {trigger}
        <SheetContent
          side="bottom"
          className="h-[85vh] p-0 rounded-t-2xl flex flex-col bg-white"
          data-testid={`${testId}-sheet`}
        >
          <SheetHeader className="px-4 pt-4 pb-2 border-b border-[#E5E2DC]">
            <SheetTitle className="text-base text-left flex items-center justify-between gap-2">
              <span>Prijscode kiezen</span>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full w-9 h-9 inline-flex items-center justify-center hover:bg-[#F0EFEA]"
                aria-label="Sluiten"
              >
                <X className="w-5 h-5" />
              </button>
            </SheetTitle>
          </SheetHeader>

          {/* Search */}
          <div className="px-4 pt-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5D7259]" />
              <input
                type="search"
                inputMode="search"
                placeholder="Zoek code, omschrijving of categorie…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-11 pl-9 pr-3 rounded-xl border border-[#E5E2DC] text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/40"
                data-testid={`${testId}-search-mobile`}
                autoFocus
              />
            </div>

            {/* Filters as horizontal scroll */}
            <div className="-mx-4 mt-3 px-4 pb-1 flex gap-1.5 overflow-x-auto no-scrollbar">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`flex-shrink-0 h-8 px-3 rounded-full text-xs border ${
                    filter === f.key
                      ? "bg-[#2C2C2A] text-white border-[#2C2C2A]"
                      : "bg-white text-[#2C2C2A] border-[#E5E2DC]"
                  }`}
                  data-testid={`${testId}-filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* List */}
          <div
            className="flex-1 overflow-y-auto px-4 py-2"
            data-testid={`${testId}-list-mobile`}
          >
            <button
              onClick={() => { onSelect(null); setOpen(false); }}
              className="w-full text-left px-3 py-3 rounded-lg border border-dashed border-[#E5E2DC] text-sm text-[#4F4F4D] mb-3"
              data-testid={`${testId}-none`}
            >
              — Geen code —
            </button>

            {Object.keys(searchedGrouped).length === 0 && (
              <div className="text-center text-sm text-[#4F4F4D] py-8">
                Geen code gevonden.
              </div>
            )}

            {Object.entries(searchedGrouped).map(([cat, codes]) => (
              <div key={cat} className="mb-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#5D7259] px-1 pb-1.5">
                  {cat}
                </div>
                <div className="space-y-1">
                  {codes.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { onSelect(p.id); setOpen(false); }}
                      className={`w-full text-left px-3 py-2 rounded-xl border transition active:bg-[#FDF0E0] min-h-[56px] ${
                        value === p.id
                          ? "border-[#D96C4A] bg-[#FDF0E0]"
                          : "border-[#E5E2DC] hover:bg-[#FAF9F6]"
                      }`}
                      data-testid={`${testId}-opt-${p.code}`}
                    >
                      <ItemRow p={p} selectedId={value} />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // -------- Desktop path: original Popover --------
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 sm:min-w-[420px]"
        align="start"
      >
        <div className="px-2 pt-2 pb-1 flex flex-wrap gap-1 border-b border-[#E5E2DC] bg-[#F9F8F6]">
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? "default" : "outline"}
              className={`h-7 px-2 rounded-full text-[11px] ${filter === f.key ? "bg-[#2C2C2A] text-white" : "border-[#E5E2DC]"}`}
              onClick={() => setFilter(f.key)}
              data-testid={`${testId}-filter-${f.key}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <Command>
          <CommandInput placeholder="Zoek code of omschrijving..." data-testid={`${testId}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>Geen code gevonden.</CommandEmpty>
            <CommandItem value="__none__" onSelect={() => { onSelect(null); setOpen(false); }} data-testid={`${testId}-none`}>
              <span className="text-[#4F4F4D]">— Geen code —</span>
            </CommandItem>
            {Object.entries(grouped).map(([cat, codes]) => (
              <CommandGroup key={cat} heading={cat}>
                {codes.map((p) => (
                  <CommandItem
                    key={p.id}
                    value={`${p.code} ${p.description} ${p.work_category || ""}`}
                    onSelect={() => { onSelect(p.id); setOpen(false); }}
                    data-testid={`${testId}-opt-${p.code}`}
                  >
                    <Check className={`w-4 h-4 mr-2 ${value === p.id ? "opacity-100" : "opacity-0"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-[#2C2C2A]">{p.description}</div>
                      <div className="text-[10px] font-mono text-[#5D7259]">{p.code} · {TRADE_LABEL[p.trade_category] || p.trade_category}</div>
                    </div>
                    {p.default_price != null && (
                      <span className="text-xs text-[#4F4F4D] ml-2 whitespace-nowrap">€{Number(p.default_price).toFixed(2)}/{p.unit}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
